import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Calculator,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Save,
  AlertCircle,
} from "lucide-react";
import { CashCountFormData } from "@/types/stock";
import { toast } from "sonner";
import { format } from "date-fns";

interface EndOfDayCashCountProps {
  expectedCash: number;
  onSave: (data: CashCountFormData) => Promise<void>;
  touchMode?: boolean;
}

interface Denomination {
  value: number;
  label: string;
  field: keyof CashCountFormData;
}

const DENOMINATIONS: Denomination[] = [
  { value: 50, label: "50 TND", field: "count50" },
  { value: 20, label: "20 TND", field: "count20" },
  { value: 10, label: "10 TND", field: "count10" },
  { value: 5, label: "5 TND", field: "count5" },
  { value: 1, label: "1 TND", field: "count1" },
  { value: 0.5, label: "500 millimes", field: "count0_500" },
  { value: 0.2, label: "200 millimes", field: "count0_200" },
  { value: 0.1, label: "100 millimes", field: "count0_100" },
  { value: 0.05, label: "50 millimes", field: "count0_050" },
];

const EndOfDayCashCount: React.FC<EndOfDayCashCountProps> = ({
  expectedCash,
  onSave,
  touchMode = false,
}) => {
  const { t } = useTranslation();
  const [counts, setCounts] = useState<CashCountFormData>({
    countDate: new Date(),
    count50: 0,
    count20: 0,
    count10: 0,
    count5: 0,
    count1: 0,
    count0_500: 0,
    count0_200: 0,
    count0_100: 0,
    count0_050: 0,
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const countedCash = useMemo(() => {
    return (
      counts.count50 * 50 +
      counts.count20 * 20 +
      counts.count10 * 10 +
      counts.count5 * 5 +
      counts.count1 * 1 +
      counts.count0_500 * 0.5 +
      counts.count0_200 * 0.2 +
      counts.count0_100 * 0.1 +
      counts.count0_050 * 0.05
    );
  }, [counts]);

  const difference = countedCash - expectedCash;
  const isOver = difference > 0;
  const isShort = difference < 0;
  const isBalanced = Math.abs(difference) < 0.01; // Account for floating point precision

  const handleCountChange = (field: keyof CashCountFormData, value: string) => {
    const numValue = parseInt(value) || 0;
    setCounts({ ...counts, [field]: Math.max(0, numValue) });
  };

  const handleSave = async () => {
    if (countedCash === 0) {
      toast.error(t("stock.cashCount.noCashCounted"));
      return;
    }

    setSaving(true);
    try {
      await onSave(counts);
      toast.success(t("stock.cashCount.saved"));
      // Reset form
      setCounts({
        countDate: new Date(),
        count50: 0,
        count20: 0,
        count10: 0,
        count5: 0,
        count1: 0,
        count0_500: 0,
        count0_200: 0,
        count0_100: 0,
        count0_050: 0,
        notes: "",
      });
    } catch (error) {
      console.error("Error saving cash count:", error);
      toast.error(t("stock.cashCount.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setCounts({
      ...counts,
      count50: 0,
      count20: 0,
      count10: 0,
      count5: 0,
      count1: 0,
      count0_500: 0,
      count0_200: 0,
      count0_100: 0,
      count0_050: 0,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            {t("stock.cashCount.title")}
          </CardTitle>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {format(new Date(), "EEEE, MMMM dd, yyyy")}
          </p>
        </CardHeader>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("stock.cashCount.expectedCash")}
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  {expectedCash.toFixed(3)} TND
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("stock.cashCount.countedCash")}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {countedCash.toFixed(3)} TND
                </p>
              </div>
              <Calculator className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("stock.cashCount.difference")}
                </p>
                <p
                  className={`text-2xl font-bold ${
                    isBalanced
                      ? "text-gray-600"
                      : isOver
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {difference >= 0 ? "+" : ""}
                  {difference.toFixed(3)} TND
                </p>
                {!isBalanced && (
                  <Badge
                    variant={isOver ? "default" : "destructive"}
                    className="mt-1"
                  >
                    {isOver
                      ? t("stock.cashCount.over")
                      : t("stock.cashCount.short")}
                  </Badge>
                )}
              </div>
              {isOver ? (
                <TrendingUp className="h-8 w-8 text-green-600" />
              ) : isShort ? (
                <TrendingDown className="h-8 w-8 text-red-600" />
              ) : (
                <AlertCircle className="h-8 w-8 text-gray-600" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Denomination Counter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t("stock.cashCount.countDenominations")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {DENOMINATIONS.map((denom) => {
              const count = counts[denom.field] as number;
              const total = count * denom.value;
              return (
                <div
                  key={denom.field}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <div className="flex-1">
                    <Label className="text-sm font-semibold">
                      {denom.label}
                    </Label>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t("stock.cashCount.total")}: {total.toFixed(3)} TND
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleCountChange(denom.field, String(count - 1))
                      }
                      disabled={count === 0}
                    >
                      -
                    </Button>
                    <Input
                      type="number"
                      min="0"
                      value={count}
                      onChange={(e) =>
                        handleCountChange(denom.field, e.target.value)
                      }
                      className="w-20 text-center"
                      size={touchMode ? undefined : "sm"}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleCountChange(denom.field, String(count + 1))
                      }
                    >
                      +
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t("stock.cashCount.notes")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder={t("stock.cashCount.notesPlaceholder")}
            value={counts.notes}
            onChange={(e) => setCounts({ ...counts, notes: e.target.value })}
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          disabled={saving || countedCash === 0}
          size={touchMode ? "lg" : "default"}
          className="flex-1"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? t("common.saving") : t("stock.cashCount.saveCount")}
        </Button>
        <Button
          variant="outline"
          onClick={handleClear}
          size={touchMode ? "lg" : "default"}
        >
          {t("common.clear")}
        </Button>
      </div>
    </div>
  );
};

export default EndOfDayCashCount;
